import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Container,
  Form,
  Row,
  Table,
  Col,
  Modal,
  Alert,
  Stack,
} from "react-bootstrap";
import { QuestionaryType } from "../../models/QuestionaryType";
import QuestionaryTypeService from "../../services/questionaryType.service";
import "./QuestionaryType.style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArchive,
  faBan,
  faBoxArchive,
  faClone,
  faEdit,
  faPlus,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import DeleteButton from "../../Helpers/deleteButton";
import { ModalQuestionaryTypeForm } from "./ModalQuestionaryTypeForm";
import ArchiveButton from "../../Helpers/archiveButton";
import ModalArchiveQuestionaryTypes from "./ModalArchiveQuestionaryTypes";

type Notification = {
  show: boolean;
  message: string;
  title: string;
  variant: "success" | "danger";
};

export default function QuestionaryTypesManager() {
  const [questionaryTypes, setQuestionaryTypes] = useState<QuestionaryType[]>(
    []
  );
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [questionaryToCloneId, setQuestionaryToCloneId] = useState<
    number | null
  >(null);
  const [newQuestionaryName, setNewQuestionaryName] = useState("");
  const [cloneError, setCloneError] = useState("");
  const [notification, setNotification] = useState<Notification | null>(null);
  const [filterText, setFilterText] = useState("");
  const [selectedQuestionaryType, setSelectedQuestionaryType] =
    useState<QuestionaryType | null>(null);
  const [modalShow, setModalShow] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);

  function onQuestionaryTypeUpdate(questionaryType: QuestionaryType) {
    QuestionaryTypeService.update(questionaryType);
  }

  function onQuestionaryTypeChange(
    description: string,
    questionaryTypeID: number
  ) {
    const myNewList = [...questionaryTypes];
    const current = myNewList.find((a) => a.id === questionaryTypeID);

    if (current) {
      current.name = description;
      setQuestionaryTypes(myNewList);
    }
  }

  function onQuestionaryTypeStatusChange(
    isActive: boolean,
    questionaryTypeID: number
  ) {
    const myNewList = [...questionaryTypes];
    const current = myNewList.find((a) => a.id === questionaryTypeID);

    if (current) {
      current.isActive = isActive;
      setQuestionaryTypes(myNewList);
      QuestionaryTypeService.update(current);
    }
  }

  async function onQuestionaryTypeRemove(questionaryType: QuestionaryType) {
    const response = await QuestionaryTypeService.remove(questionaryType);

    if (response.ok) {
      setNotification({
        show: true,
        variant: "success",
        title: "Éxito",
        message: "Tipo de cuestionario eliminado correctamente.",
      });
      await loadQuestionaryTypes();
    } else {
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: await response.text(),
      });
    }
  }

  const handleCloneClick = (idToClone: number) => {
    setQuestionaryToCloneId(idToClone);
    setNewQuestionaryName("");
    setCloneError("");
    setShowCloneModal(true);
  };

  const handleCloseCloneModal = () => {
    setShowCloneModal(false);
    setQuestionaryToCloneId(null);
    setNewQuestionaryName("");
    setCloneError("");
  };

  const handleCloneSave = async () => {
    if (!newQuestionaryName.trim()) {
      setCloneError("El nombre del nuevo cuestionario no puede estar vacío.");
      return;
    }

    if (questionaryToCloneId === null) {
      setCloneError("No se ha seleccionado un cuestionario para clonar.");
      return;
    }

    try {
      const request = {
        questionaryTypeIdToClone: questionaryToCloneId,
        newQuestionaryName: newQuestionaryName,
      };

      const successMessage = await QuestionaryTypeService.clone(request);
      setNotification({
        show: true,
        variant: "success",
        title: "Éxito",
        message: successMessage ? successMessage : "Clonado",
      });

      //Actualizar la lista
      await loadQuestionaryTypes();
      handleCloseCloneModal();
    } catch (error: any) {
      console.error("Error cloning questionary type:", error.message);
      setCloneError(`Error al clonar: ${error.message}`);
    }
  };

  async function loadQuestionaryTypes() {
    try {
      const data = await QuestionaryTypeService.getQuestionaryTypes();
      setQuestionaryTypes(data);
    } catch (error: any) {
      console.error("Error loading questionary types:", error.message);
      // Optionally display an error message to the user
    }
  }

  const getFilteredQuestionaryTypes = () => {
    const search = filterText.toLowerCase();

    return questionaryTypes.filter((qt) => {
      return (
        qt.isArchived === false &&
        (qt.name.toLowerCase().includes(search) ||
          qt.id?.toString().includes(search))
      );
    });
  };

  const handleSave = async (questionaryType: QuestionaryType) => {
    try {
      if (questionaryType.id) {
        const response = await QuestionaryTypeService.update(questionaryType);
        if (response.ok) {
          setNotification({
            show: true,
            variant: "success",
            title: "Éxito",
            message: "Tipo de auditoría actualizada correctamente.",
          });
          setSelectedQuestionaryType(null);
          setModalShow(false);
          await loadQuestionaryTypes();
          return;
        } else {
          const errorMessage = await response.text();
          throw new Error(
            errorMessage || "Error al actualizar tipo de cuestionario"
          );
        }
      } else {
        const response = await QuestionaryTypeService.insert(questionaryType);

        if (response.ok) {
          setNotification({
            show: true,
            variant: "success",
            title: "Éxito",
            message: "Tipo de cuestionario guardado correctamente.",
          });
          setSelectedQuestionaryType(null);
          setModalShow(false);
          await loadQuestionaryTypes();
          return;
        } else {
          const errorMessage = await response.text();
          throw new Error(
            errorMessage || "Error al agregar tipo de cuestionario."
          );
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: error instanceof Error ? error.message : "",
      });
    }
  };

  const handleArchive = async (questionaryType: QuestionaryType) => {
    const response = await QuestionaryTypeService.archive(questionaryType);

    if (response.ok) {
      setNotification({
        show: true,
        variant: "success",
        title: "Éxito",
        message: questionaryType.isArchived
          ? "Tipo de cuestionario restaurado correctamente."
          : "Tipo de cuestionario archivado correctamente.",
      });
      await loadQuestionaryTypes();
      return;
    } else {
      const errorMessage = await response.text();
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: errorMessage || "Error al archivar tipo de cuestionario.",
      });
    }
  };

  useEffect(() => {
    loadQuestionaryTypes();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <>
      <div className="notification-container">
        {notification && (
          <Alert
            variant={notification.variant}
            onClose={() => setNotification(null)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{notification.title}</Alert.Heading>
            {notification.message}
          </Alert>
        )}
      </div>

      <Container className="pt-3">
        <Card>
          <Card.Body>
            <Card.Title>Tipos de cuestionario</Card.Title>
            <hr />
            <Row className="mb-3 g-3 align-items-center">
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Col>
              <Col
                md={2}
                className="d-flex justify-content-md-start align-items-center"
              >
                <Stack direction="horizontal" gap={3}>
                  <Button
                    onClick={() => {
                      setShowArchivedModal(true);
                    }}
                    variant="primary"
                    className="d-flex align-items-center"
                  >
                    <FontAwesomeIcon icon={faArchive} className="white" />
                    <span className="ms-2">Archivados</span>
                  </Button>
                </Stack>
              </Col>
              <Col
                md={5}
                className="d-flex justify-content-md-end align-items-center"
              >
                <Button
                  onClick={() => {
                    setSelectedQuestionaryType(null);
                    setModalShow(true);
                  }}
                  variant="primary"
                >
                  <FontAwesomeIcon icon={faPlus} className="white" />
                  <span className="ms-2">Nuevo tipo</span>
                </Button>
              </Col>
            </Row>
            <Row>
              <Col>
                <Table striped bordered hover>
                  <thead>
                    <tr className="text-center">
                      <th>Id</th>
                      <th>Descripción</th>
                      <th>Activo</th>
                      <th> Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredQuestionaryTypes().map((qt) => (
                      <tr key={qt.id}>
                        <td className="text-center">{qt.id}</td>
                        <td>{qt.name}</td>
                        <td className="text-center">
                          <Form.Check
                            type="switch"
                            id={`isActiveSwitch-${qt.id}`}
                            checked={qt.isActive}
                            onChange={(e) => {
                              onQuestionaryTypeStatusChange(
                                e.target.checked,
                                qt.id!
                              );
                            }}
                          />
                        </td>
                        <td className="text-center">
                          <Stack
                            direction="horizontal"
                            gap={2}
                            className="justify-content-center"
                          >
                            <Button
                              variant="primary"
                              size="sm"
                              title="Editar"
                              onClick={() => {
                                setSelectedQuestionaryType(qt);
                                setModalShow(true);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="white"
                              />
                            </Button>
                            <Button
                              variant="secondary"
                              active
                              size="sm"
                              title="Clonar"
                              onClick={() => handleCloneClick(qt.id!)}
                            >
                              <FontAwesomeIcon
                                icon={faClone}
                                className="white"
                              />
                            </Button>
                            <ArchiveButton
                              title="Archivar"
                              description="¿Estás seguro que deseas archivar el registro?"
                              iconButton={faBoxArchive}
                              onClick={() => handleArchive(qt)}
                            />
                            <DeleteButton
                              onClick={() => {
                                onQuestionaryTypeRemove(qt);
                              }}
                            />
                          </Stack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>

      <ModalQuestionaryTypeForm
        show={modalShow}
        onHide={() => setModalShow(false)}
        onSave={handleSave}
        questionaryType={selectedQuestionaryType}
        title={
          selectedQuestionaryType
            ? "Editar tipo de cuestionario"
            : "Nuevo tipo de cuestionario"
        }
      />

      {/** Modal de archivados */}
      <ModalArchiveQuestionaryTypes
        onHide={() => setShowArchivedModal(false)}
        show={showArchivedModal}
        questionaryType={questionaryTypes}
        onRestore={handleArchive}
      />
      {/* Clone Modal */}
      <Modal
        show={showCloneModal}
        onHide={handleCloseCloneModal}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>Clonar tipo de cuestionario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cloneError && <p className="text-danger">{cloneError}</p>}
          <Form.Group className="mb-3">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              type="text"
              value={newQuestionaryName}
              onChange={(e) => setNewQuestionaryName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCloneModal}>
            <FontAwesomeIcon
              icon={faBan}
              style={{
                paddingRight: "3px",
              }}
            />
            <span className="ms-2">Cancelar</span>
          </Button>
          <Button variant="primary" onClick={handleCloneSave}>
            <FontAwesomeIcon
              icon={faSave}
              style={{
                paddingRight: "3px",
              }}
            />
            <span className="ms-2">Guardar</span>
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
